'use client';

import React from 'react';
import type {
  FlatHeatmapMetric,
  GroupedHeatmapMetric,
} from 'src/hooks/heatmap';

interface HeatmapMetricListProps {
  groupedItems: Record<number, GroupedHeatmapMetric>;
  selectedMetricCode: string | undefined;
  showHeatmap: boolean;
  requestCodeFor: (code: string) => string | undefined;
  onSelectAllOpportunities: () => void;
  onSelectMetric: (metric: FlatHeatmapMetric) => void;
  onTurnOff: () => void;
}

const HeatmapMetricList: React.FC<HeatmapMetricListProps> = ({
  groupedItems,
  selectedMetricCode,
  showHeatmap,
  requestCodeFor,
  onSelectAllOpportunities,
  onSelectMetric,
  onTurnOff,
}) => {
  const isAllOpportunitiesSelected = showHeatmap && !selectedMetricCode;

  return (
    <>
      <div role="listbox" aria-label="Heatmap metrics">
        {/* All Opportunities option at the top */}
        <div className="border-b border-neutral-100">
          <button
            onClick={onSelectAllOpportunities}
            role="option"
            aria-selected={isAllOpportunitiesSelected}
            data-testid="heatmap-all-opportunities-button"
            className={`
              w-full px-4 py-3 text-left text-sm transition-colors
              ${
                isAllOpportunitiesSelected
                  ? 'bg-sky-50 text-sky-600 font-medium border-l-2 border-sky-500'
                  : 'text-neutral-700 hover:bg-neutral-50 border-l-2 border-transparent'
              }
            `}
          >
            All Opportunities
          </button>
        </div>

        {/* Metric options */}
        {Object.entries(groupedItems).map(([metricId, { label, options }]) => (
          <div
            key={metricId}
            className="border-b border-neutral-100"
            role="group"
            aria-label={label}
          >
            <div className="px-4 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wide bg-neutral-50">
              {label}
            </div>
            {options.map((opt) => (
              <button
                key={opt.code}
                onClick={() => onSelectMetric(opt)}
                role="option"
                aria-selected={selectedMetricCode === requestCodeFor(opt.code)}
                data-testid={`heatmap-metric-${opt.code}`}
                className={`
                  w-full px-4 py-3 text-left text-sm transition-colors
                  ${
                    selectedMetricCode === requestCodeFor(opt.code)
                      ? 'bg-sky-50 text-sky-600 font-medium border-l-2 border-sky-500'
                      : 'text-neutral-700 hover:bg-neutral-50 border-l-2 border-transparent'
                  }
                `}
              >
                {opt.label}
              </button>
            ))}
          </div>
        ))}

        {/* Empty state */}
        {Object.keys(groupedItems).length === 0 && (
          <div
            className="px-4 py-8 text-center text-neutral-500 text-sm"
            data-testid="heatmap-empty-state"
          >
            No metrics available
          </div>
        )}
      </div>

      {/* Turn Off Heatmap */}
      {showHeatmap && (
        <div className="border-t border-neutral-200 p-3">
          <button
            onClick={onTurnOff}
            aria-label="Turn off heatmap"
            data-testid="heatmap-turn-off-button"
            className="w-full px-4 py-2 text-sm font-medium text-neutral-600 bg-white border border-neutral-300 rounded-full hover:bg-neutral-50 transition-colors"
          >
            Turn Off Heatmap
          </button>
        </div>
      )}
    </>
  );
};

export default HeatmapMetricList;
